import DOMPurify from 'isomorphic-dompurify';

/**
 * Admin-authored rich text (Quill HTML) is stored raw and rendered through
 * `dangerouslySetInnerHTML` on guest-facing pages. Every church shares one
 * origin with `/admin/*`, and `onark-token` is a JS-readable cookie, so script
 * that reaches a guest page runs with the viewer's session in reach.
 *
 * Runs on BOTH ends. On save, so nothing new lands dirty in the column; on
 * render, because save-side alone cannot reach the rows written before it
 * existed, nor the paths that do not go through a mutation (history restore,
 * course clone). Neither half is redundant: render-only leaves the database
 * dirty, save-only leaves the existing rows unsanitized.
 *
 * Lives in @onark/shared because the Koa api (save) and the Next frontend
 * (render) must agree exactly. Two allowlists that drifted would mean content
 * an admin can save but a guest never sees.
 *
 * The allowlist is deliberately the union of what the Quill toolbars can
 * produce AND what production rows already contain: content predates the
 * current toolbars (migrated Squarespace/Thinkific courses carry inline color,
 * headers, lists and external images), so a narrower list would silently strip
 * formatting churches can see.
 */
const ALLOWED_TAGS = [
  'p',
  'br',
  'span',
  'div',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'sub',
  'sup',
  'blockquote',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ol',
  'ul',
  'li',
  'a',
  'img',
];

// `class` carries Quill's ql-align/ql-indent formatting; `style` carries the
// inline color/background 343 Section.text rows already rely on. DOMPurify
// scrubs the style value itself (url(javascript:), expression(), behavior).
const ALLOWED_ATTR = [
  'href',
  'target',
  'rel',
  'src',
  'alt',
  'width',
  'height',
  'class',
  'style',
  // Quill writes `<span class="ql-ui" contenteditable="false">` into every list
  // item. It is inert outside the editor and carries no script, but keeping it
  // means the sanitizer is byte-identical on the 28 production rows that have
  // one, so a save cannot churn content it is not fixing.
  'contenteditable',
];

/**
 * Everything an `<img>` may keep. Images are the one tag an admin can point at
 * a remote origin, so they get a tighter list than the document-wide one above:
 * no `class`, no `style`, and in particular no `on*` handler, which is the
 * `<img src=x onerror=...>` payload this whole guard exists for.
 */
const ALLOWED_IMG_ATTR = new Set(['src', 'alt', 'width', 'height']);

/**
 * http/https/mailto/tel only. Notably excludes `javascript:` and `data:`.
 */
const ALLOWED_URI_REGEXP =
  /^(?:https?:|mailto:|tel:|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

/**
 * Registered once at module load, and the only place `<img>` is narrowed.
 *
 * Two things DOMPurify's declarative config cannot express:
 *  - `ALLOWED_ATTR` is document-wide, so the tighter image list has to be
 *    applied per node.
 *  - `data:` URIs are allowed on `src` for DOMPurify's DATA_URI_TAGS (img,
 *    audio, video) REGARDLESS of ALLOWED_URI_REGEXP, so the regexp above does
 *    not cover a pasted base64 image on its own. Section images upload to S3
 *    and store a URL (see EditorInput's `imageUpload`); an inline data: URI
 *    would be served in full to every guest on the section.
 */
DOMPurify.addHook('afterSanitizeAttributes', node => {
  if (node.nodeName !== 'IMG' || typeof node.getAttribute !== 'function') {
    return;
  }

  // `attributes` is a live NamedNodeMap, so snapshot the names before removing
  // any of them. Typed here because @onark/shared builds without the DOM lib.
  const names = Array.from(
    node.attributes as ArrayLike<{ name: string }>,
    attr => attr.name
  );

  for (const name of names) {
    if (!ALLOWED_IMG_ATTR.has(name.toLowerCase())) {
      node.removeAttribute(name);
    }
  }

  if (/^\s*data:/i.test(node.getAttribute('src') || '')) {
    node.removeAttribute('src');
  }
});

export function sanitizeRichText(html: string | null | undefined): string {
  if (!html) return '';

  return DOMPurify.sanitize(html, {
    ALLOWED_ATTR,
    ALLOWED_TAGS,
    ALLOWED_URI_REGEXP,
    // Belt and braces: these are excluded by the tag allowlist above, but
    // naming them keeps the intent obvious to the next reader.
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
  });
}
